import React from 'react';
import {
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {t} from 'i18next';
import './FunctionalityTab.scss';
import {ReactComponent as CheckedIcon} from '../../../../../src/assets/images/icon-checked.svg';
import {ReactComponent as UnCheckedIcon} from '../../../../../src/assets/images/icon-unchecked.svg';
import {ReactComponent as InactiveIcon} from '../../../../../src/assets/images/icon-unchecked-inactive.svg';
import {ReactComponent as Indeterminate} from '../../../../../src/assets/images/icon-checked-indeterminate.svg';
import {AI_REVIEW_ENABLED} from '../../../ai/review/constant/AiReviewConstants';
import {
  getRedactionCheckboxState,
  REDACTIONS_PARENT_KEY,
  REDACTION_PERMISSION_KEYS,
} from '../../../../api/types/user.type';

interface Permission {
  label: string;
  disabled?: boolean | 'disabled';
  checked?: boolean | 'disabled';
  key: string;
  items?: Permission[];
}

interface Section {
  title: string;
  items: Permission[];
}

interface CheckboxState {
  checked: boolean;
  indeterminate: boolean;
}

interface FunctionalityProps {
  permissions: Record<string, boolean | 'disabled'>;
  onRightsChange?: (
    updatedRights: Record<string, boolean | 'disabled'>
  ) => void;
}

const COMPUTED_PARENT_KEYS = new Set(['canGenAi']);

// Native Redaction stays in the payload but is not shown. Its value always
// matches the Image Redaction checkbox.
const alignNativeRedactionWithImage = (
  rights: Record<string, boolean | 'disabled'>
): Record<string, boolean | 'disabled'> => {
  if (!('canImageRedaction' in rights)) {
    return rights;
  }

  return {
    ...rights,
    canNativeRedaction: rights.canImageRedaction,
  };
};

const getAllChildKeys = (item: Permission): string[] => {
  if (!item.items) return [];
  return item.items.flatMap((child) => [child.key, ...getAllChildKeys(child)]);
};

const computeStateFromChildren = (
  item: Permission,
  permissions: Record<string, boolean | 'disabled'>,
  level = 0
): CheckboxState => {
  if (!item.items || item.items.length === 0) {
    return {checked: permissions[item.key] === true, indeterminate: false};
  }

  if (level === 0 && item.key === REDACTIONS_PARENT_KEY) {
    return getRedactionCheckboxState(alignNativeRedactionWithImage(permissions));
  }

  if (level === 0 && COMPUTED_PARENT_KEYS.has(item.key)) {
    const enabledChildKeys = getAllChildKeys(item).filter(
      (key) => permissions[key] !== 'disabled'
    );

    if (enabledChildKeys.length === 0) {
      return {checked: false, indeterminate: false};
    }

    const allChecked = enabledChildKeys.every(
      (key) => permissions[key] === true
    );

    const noneChecked = enabledChildKeys.every(
      (key) => permissions[key] !== true
    );

    return {
      checked: allChecked,
      indeterminate: !allChecked && !noneChecked,
    };
  }

  return {checked: permissions[item.key] === true, indeterminate: false};
};

const PermissionCheckbox: React.FC<{
  item: Permission;
  onToggle: (key: string, item: Permission, level: number) => void;
  permissions: Record<string, boolean | 'disabled'>;
  level?: number;
}> = ({item, onToggle, permissions, level = 0}) => {
  const {checked, indeterminate} = computeStateFromChildren(
    item,
    permissions,
    level
  );

  return (
    <Box className={`permission-level-${level}`}>
      <FormControlLabel
        data-testid={`permission-level-${item.label}`}
        key={item.label}
        control={
          <Checkbox
            data-testid={`functionality-tab-permission-${item.key}`}
            checked={!item.disabled && !!checked}
            indeterminate={indeterminate}
            disabled={item.disabled === 'disabled' ? true : item.disabled}
            onChange={() => onToggle(item.key, item, level)}
            className="checkbox"
            icon={item.disabled ? <InactiveIcon /> : <UnCheckedIcon />}
            checkedIcon={<CheckedIcon />}
            indeterminateIcon={<Indeterminate />}
          />
        }
        label={
          <Typography
            className={`typo-text body3_size section-label ${
              item.disabled ? 'disabled' : ''
            }`}
            data-testid={`functionality-tab-label-${item.label.replaceAll(' ', '-')}`}
          >
            {item.label}
          </Typography>
        }
      />
      {item.items && (
        <Box className="child-permissions">
          {item.items.map((child) => (
            <PermissionCheckbox
              key={child.key}
              item={child}
              permissions={permissions}
              onToggle={onToggle}
              level={level + 1}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

const SectionBlock: React.FC<{
  section: Section;
  onToggle: (key: string, item: Permission, level: number) => void;
  permissions: Record<string, boolean | 'disabled'>;
}> = ({section, onToggle, permissions}) => (
  <Box className="section">
    <Typography
      className="typo-text subhead4_size section-title"
      data-testid={`functionality-tab-typography-section-title-${section.title.replaceAll(' ', '-')}`}
    >
      {section.title}
    </Typography>
    <FormGroup>
      {section.items.map((item) => (
        <PermissionCheckbox
          key={item.key}
          item={item}
          permissions={permissions}
          onToggle={onToggle}
        />
      ))}
    </FormGroup>
  </Box>
);

const FunctionalityTab: React.FC<FunctionalityProps> = ({
  permissions,
  onRightsChange,
}) => {
  const sections: Section[][] = [
    [
      {
        title: t('userManagement.editUser.functionality.general'),
        items: [
          {
            key: 'canManage',
            label: t('userManagement.editUser.functionality.manage'),
            checked:
              permissions.canManage === 'disabled'
                ? false
                : permissions.canManage,
            disabled: permissions.canManage === 'disabled',
          },
          {
            key: 'canManageProjects',
            label: t(
              'userManagement.editUser.functionality.manageDomainProjects'
            ),
            checked:
              permissions.canManageProjects === 'disabled'
                ? false
                : permissions.canManageProjects,
            disabled: permissions.canManageProjects === 'disabled',
          },
          {
            key: 'canIngestions',
            label: t('userManagement.editUser.functionality.ingestions'),
            checked:
              permissions.canIngestions === 'disabled'
                ? false
                : permissions.canIngestions,
            disabled: permissions.canIngestions === 'disabled',
            items:[
              {
                key: 'canAPO',
                label: t('userManagement.editUser.functionality.apoupdates'),
                checked:
                  permissions.canAPO === 'disabled'
                    ? false
                    : permissions.canAPO,
                disabled: permissions.canAPO === 'disabled',
              },
            ]
          },
          {
            key: 'canProductions',
            label: t('userManagement.editUser.functionality.productions'),
            checked:
              permissions.canProductions === 'disabled'
                ? false
                : permissions.canProductions,
            disabled: permissions.canProductions === 'disabled',
          },
          {
            key: 'canSearching',
            label: t('userManagement.editUser.functionality.searching'),
            checked: permissions.canSearching,
          },
          {
            key: 'canConceptExplorer',
            label: t('userManagement.editUser.functionality.conceptExplorer'),
            checked: permissions.canConceptExplorer,
          },
          {
            key: 'canCommunicationsExplorer',
            label: t(
              'userManagement.editUser.functionality.communicationsExplorer'
            ),
            checked: permissions.canCommunicationsExplorer,
          },
          {
            key: 'canProview',
            label: t('userManagement.editUser.functionality.categorize'),
            checked:
              permissions.canProview === 'disabled'
                ? false
                : permissions.canProview,
            disabled: permissions.canProview === 'disabled',
          },
          {
            key: 'canDatasets',
            label: t('userManagement.editUser.functionality.datasets'),
            checked:
              permissions.canDatasets === 'disabled'
                ? false
                : permissions.canDatasets,
            disabled: permissions.canDatasets === 'disabled',
          },
          {
            key: 'canExportToTrueLaw',
            label: t('userManagement.editUser.functionality.exportTrueLawAI'),
            checked:
              permissions.canExportToTrueLaw === 'disabled'
                ? false
                : permissions.canExportToTrueLaw,
            disabled: permissions.canExportToTrueLaw === 'disabled',
          },
          {
            key: 'canGenAi',
            label: t('userManagement.editUser.functionality.generativeAI'),
            checked:
              permissions.canGenAi === 'disabled'
                ? false
                : permissions.canGenAi,
            disabled: permissions.canGenAi === 'disabled',
            items: [
              {
                key: 'canAIInvestigate',
                label: t('userManagement.editUser.functionality.aiInvestigate'),
                checked:
                  permissions.canAIInvestigate === 'disabled'
                    ? false
                    : permissions.canAIInvestigate,
                disabled: permissions.canAIInvestigate === 'disabled',
                items: [
                  {
                    key: 'canManageIndices',
                    label: t(
                      'userManagement.editUser.functionality.manageIndices'
                    ),
                    checked:
                      permissions.canManageIndices === 'disabled'
                        ? false
                        : permissions.canManageIndices,
                    disabled: permissions.canManageIndices === 'disabled',
                  },
                ],
              },
              {
                key: 'canAIReview',
                label: t('userManagement.editUser.functionality.aiReview'),
                checked:
                  permissions.canAIReview === 'disabled'
                    ? false
                    : permissions.canAIReview,
                disabled: permissions.canAIReview === 'disabled',
                items: [
                  {
                    key: 'canManageProtocols',
                    label: t(
                      'userManagement.editUser.functionality.manageProtocols'
                    ),
                    checked:
                      permissions.canManageProtocols === 'disabled'
                        ? false
                        : permissions.canManageProtocols,
                    disabled: permissions.canManageProtocols === 'disabled',
                  },
                ],
              },
              {
                key: 'canAIBatchSummarize',
                label: t(
                  'userManagement.editUser.functionality.aibatchSummarize'
                ),
                checked:
                  permissions.canAIBatchSummarize === 'disabled'
                    ? false
                    : permissions.canAIBatchSummarize,
                disabled: permissions.canAIBatchSummarize === 'disabled',
              },
            ],
          },
          
        ],
      },
    ],
    [
      {
        title: t('userManagement.editUser.functionality.reports'),
        items: [
          {
            key: 'canAllReports',
            label: t('userManagement.editUser.functionality.allReports'),
            checked:
              permissions.canAllReports === 'disabled'
                ? false
                : permissions.canAllReports,
            disabled: permissions.canAllReports === 'disabled',
          },
        ],
      },
      {
        title: t('userManagement.editUser.functionality.docViewControls'),
        items: [
          {
            key: 'canDownloadNative',
            label: t('userManagement.editUser.functionality.downloadNative'),
            checked: permissions.canDownloadNative,
          },
          {
            key: REDACTIONS_PARENT_KEY,
            label: t('userManagement.editUser.functionality.redactions'),
            items: [
              {
                key: 'canImageRedaction',
                label: t(
                  'userManagement.editUser.functionality.imageRedaction'
                ),
                checked: permissions.canImageRedaction,
              },
              // Native Redaction checkbox is hidden. canNativeRedaction is sent
              // with the same value as canImageRedaction.
              // {
              //   key: 'canNativeRedaction',
              //   label: t(
              //     'userManagement.editUser.functionality.nativeRedaction'
              //   ),
              //   checked: permissions.canNativeRedaction,
              // },
              {
                key: 'canAudioRedaction',
                label: t(
                  'userManagement.editUser.functionality.audioRedaction'
                ),
                checked: permissions.canAudioRedaction,
              },
            ],
          },
          {
            key: 'canHighlighting',
            label: t('userManagement.editUser.functionality.highlighting'),
            checked: permissions.canHighlighting,
          },
          {
            key: 'canReviewerRemarks',
            label: t('userManagement.editUser.functionality.reviewerRemarks'),
            checked: permissions.canReviewerRemarks,
          },
          {
            key: 'canAnalyticsPanels',
            label: t('userManagement.editUser.functionality.analyticsPanels'),
            checked: permissions.canAnalyticsPanels,
          },
        ],
      },
    ],
  ];

  const updateGenAiParentState = (
    genAiItem: Permission,
    updatedRights: Record<string, boolean | 'disabled'>
  ) => {
    const allChildKeys = getAllChildKeys(genAiItem);

    const enabledKeys = allChildKeys.filter(
      (key) => updatedRights[key] !== 'disabled'
    );

    const allUnchecked = enabledKeys.every(
      (key) => updatedRights[key] === false
    );

    const anyChecked = enabledKeys.some((key) => updatedRights[key] === true);

    if (allUnchecked) {
      updatedRights['canGenAi'] = false;
    } else if (anyChecked) {
      updatedRights['canGenAi'] = true;
    }
  };

  const handleToggle = (key: string, item: Permission) => {
    if (permissions[key] === 'disabled') return;

    const updatedRights = {...permissions};
    const currentChecked =
      key === REDACTIONS_PARENT_KEY
        ? getRedactionCheckboxState(alignNativeRedactionWithImage(permissions))
            .checked
        : COMPUTED_PARENT_KEYS.has(key)
          ? computeStateFromChildren(item, permissions, 0).checked
          : permissions[key] === true;

    const newValue = !currentChecked;

    const genAiItem = sections[0][0].items.find((i) => i.key === 'canGenAi');
    if (key === 'canGenAi') {
      updatedRights[key] = newValue;

      getAllChildKeys(item).forEach((childKey) => {
        if (permissions[childKey] !== 'disabled') {
          updatedRights[childKey] = newValue;
        }
      });

      onRightsChange?.(alignNativeRedactionWithImage(updatedRights));
      return;
    }

    if (key === REDACTIONS_PARENT_KEY) {
      const {checked} = getRedactionCheckboxState(
        alignNativeRedactionWithImage(updatedRights)
      );
      const newValue = !checked;

      REDACTION_PERMISSION_KEYS.forEach((childKey) => {
        if (permissions[childKey] !== 'disabled') {
          updatedRights[childKey] = newValue;
        }
      });

      onRightsChange?.(alignNativeRedactionWithImage(updatedRights));
      return;
    }

    if (genAiItem && genAiItem.items) {
      const firstLevel = genAiItem.items.find((i) => i.key === key);
      if (firstLevel) {
        updatedRights[key] = newValue;

        if (!newValue && firstLevel.items) {
          getAllChildKeys(firstLevel).forEach((childKey) => {
            if (permissions[childKey] !== 'disabled') {
              updatedRights[childKey] = false;
            }
          });
        }
        updateGenAiParentState(genAiItem, updatedRights);

        onRightsChange?.(alignNativeRedactionWithImage(updatedRights));
        return;
      }

      const parentOfNested = genAiItem.items.find((i) =>
        i.items?.some((grandchild) => grandchild.key === key)
      );

      if (parentOfNested) {
        if (!permissions[parentOfNested.key] && newValue) return;

        updatedRights[key] = newValue;

        updateGenAiParentState(genAiItem, updatedRights);

        onRightsChange?.(alignNativeRedactionWithImage(updatedRights));
        return;
      }
    }

    updatedRights[key] = newValue;

    if (item.items) {
      getAllChildKeys(item).forEach((childKey) => {
        updatedRights[childKey] = newValue;
      });
    }

    onRightsChange?.(alignNativeRedactionWithImage(updatedRights));
  };

  return (
    <Box className="functionality-tab">
      {sections.map((column, colIdx) => (
        <Box key={colIdx} className="section-wrapper">
          {column.map((section) => (
            <SectionBlock
              key={section.title}
              section={section}
              onToggle={handleToggle}
              permissions={permissions}
            />
          ))}
        </Box>
      ))}
    </Box>
  );
};

export default FunctionalityTab;
